/* Customer shop API hooks (TanStack Query).
 *
 * Plan §"Existing API reference":
 *   GET  /api/{tenantCode}/v1/products?kioskId=N
 *   POST /api/{tenantCode}/v1/products/lookup-barcode
 *        body: { "barcode": string, "kioskId": "<digits-only-string>" }
 *
 * The lookup-barcode body MUST send `kioskId` as a string of digits because
 * the backend `barcodeLookupSchema` (productSchemas.ts:132–137) declares it as
 * `z.string().regex(/^\d+$/).transform(parseInt)`. Passing a JSON number would
 * fail validation. See plan todo `barcode-lookup-numeric-string-rule`.
 *
 * Refetch policy (plan §"Stock refresh"): override the global
 * `refetchOnWindowFocus: false` from `AppDataProvider` so the cart line
 * warning state recomputes whenever the customer refocuses the tab.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { useCallback, useMemo, useRef } from 'react';
import { computePollRetryDelayMs, ensureAcceptedCurrenciesForCheckout } from 'pi-kiosk-shared';
import { resolvePromotionsProgramEnabled } from 'pi-kiosk-shared/promo/resolvePromotionsProgramEnabled';
import { ApiError } from '../../shared/api/ApiClient.js';
import { resolvePublicApiFailureMessage } from '../../shared/errors/publicApiFailureMessage.js';
import { useApiClient } from '../../shared/providers/AppDataProvider.js';
import { shouldRefetchCustomerOnReconnect } from '../../shared/providers/customerQueryClient.js';
import { customerQueryKeys } from '../../shared/api/queryKeys.js';
import { useTenantCode } from '../../shared/tenant/TenantProvider.js';
import type {
  KioskPublicCatalogMedia,
  LocalizedNameMap,
  PaymentSurfaceReadiness,
} from 'pi-kiosk-shared';
import type { CheckoutSubMode } from '../checkout/checkoutApi.js';
import {
  parseSalesPointCustomerPresence,
  type SalesPointCustomerPresence,
} from '../shops/customerPresence.js';
import type { BarcodeLookupResult, CatalogProduct } from './types.js';
import { useShopCatalogRealtimeSync } from './hooks/useShopCatalogRealtimeSync.js';

/**
 * PO-2 / IMG-014 fallback when SSE is disconnected or entitlement-denied —
 * confirmed 30s poll interval. Primary-image pixels then update on the next
 * successful poll (no realtime cache-bust / urgent refetch path).
 */
export const PRODUCT_CATALOG_REFETCH_INTERVAL_MS = 30_000;
export const PRODUCT_CATALOG_STALE_TIME_SSE_MS = 300_000;

/** Absent-array fallback for sales-point wire — all three mobile-first sub-modes (G10). */
export const DEFAULT_ENABLED_MOBILE_FIRST_CHECKOUT_SUB_MODES: readonly CheckoutSubMode[] = [
  'PREPAY_COLLECT_LATER',
  'PAY_NOW_SELF_SERVICE',
  'PAY_NOW_STAFF_HANDOFF',
];

interface ProductsApiResponse {
  products: CatalogProduct[];
}

export function buildShopProductsQueryOptions(
  kioskId: number | null,
  cacheKey: readonly unknown[],
  sseConnected = false,
): Omit<UseQueryOptions<CatalogProduct[], Error>, 'queryFn'> {
  return {
    queryKey: cacheKey,
    enabled: kioskId !== null && kioskId > 0,
    staleTime: sseConnected
      ? PRODUCT_CATALOG_STALE_TIME_SSE_MS
      : PRODUCT_CATALOG_REFETCH_INTERVAL_MS,
    refetchInterval: sseConnected ? false : PRODUCT_CATALOG_REFETCH_INTERVAL_MS,
    refetchOnMount: false,
    refetchOnWindowFocus: true,
    // G18 / G8 — honor storm + guest/network latch (BAN reconnect catalog stampede).
    refetchOnReconnect: shouldRefetchCustomerOnReconnect,
    structuralSharing: false,
    retry: (failureCount, error) =>
      failureCount < 3 && !(typeof error === 'object' && error !== null && 'status' in error && error.status === 429),
    retryDelay: (attempt, error) =>
      computePollRetryDelayMs(attempt, error, { baseMs: 2_000, maxMs: 120_000 }),
  };
}

interface CustomerShopConfigResponse {
  catalogMedia: KioskPublicCatalogMedia;
}

interface CustomerSalesPointCommerceCapabilities {
  readonly promotionsProgramEnabled?: boolean;
}

interface CustomerSalesPointApiResponse {
  salesPointId: number;
  code: string | null;
  name: string;
  nameLocales?: LocalizedNameMap | null;
  location: string | null;
  /** A20 — ONLINE_ONLY ⇒ FE forces location null (no ops address leak). */
  customerPresence?: SalesPointCustomerPresence | string | null;
  salesPointOperationalMode: 'PRODUCTS' | 'DONATION';
  salesPointInteractionMode?: 'CUSTOMER_FACING' | 'STAFF_OPERATED';
  currencyAcceptanceMode?: 'SINGLE' | 'MULTI';
  acceptedCurrencies?: string[];
  /** Tenant heal preferred for empty accepted (forward-compat / re-ensure). */
  defaultCurrency?: string;
  commerceConfigJson?: Record<string, unknown> | null;
  commerceCapabilities?: CustomerSalesPointCommerceCapabilities | null;
  enabledMobileFirstCheckoutSubModes?: Array<CheckoutSubMode>;
  paymentRailsMobile?: {
    bankTransfer?: boolean;
    gateway?: boolean;
  };
  payableVerifiedMethodCount?: number;
  paymentSurfaceReadiness?: PaymentSurfaceReadiness;
  payOnSpotProximityNoticeMinutes?: number;
  allowedPickupHandoffModes?: Array<
    'AUTO_ON_PAYMENT' | 'CUSTOMER_TAP' | 'SCAN_AT_STAND' | 'STAFF_SCAN'
  >;
  defaultPickupHandoffMode?: 'AUTO_ON_PAYMENT' | 'CUSTOMER_TAP' | 'SCAN_AT_STAND' | 'STAFF_SCAN';
  isActive: boolean;
  redirectTo?: string;
}

/** Exported for unit tests (G3-T) — maps GET /customer/sales-points/by-id payload. */
export function mapSalesPointToShopKiosk(
  response: CustomerSalesPointApiResponse
): ShopKioskBySlugResponse {
  const customerPresence = parseSalesPointCustomerPresence(response.customerPresence);
  // A20 — ONLINE_ONLY never surfaces ops location in shop shell meta (defense-in-depth).
  const location =
    customerPresence === 'ONLINE_ONLY'
      ? null
      : typeof response.location === 'string'
        ? response.location
        : null;
  return {
    kioskId: response.salesPointId,
    code: response.code,
    name: response.name,
    nameLocales: response.nameLocales ?? null,
    location,
    customerPresence,
    kioskOperationalMode: response.salesPointOperationalMode,
    salesPointInteractionMode: response.salesPointInteractionMode,
    currencyAcceptanceMode: response.currencyAcceptanceMode ?? 'SINGLE',
    // Empty/null accepted heals with tenant preferred (else CZK) so shop never blocks.
    acceptedCurrencies: ensureAcceptedCurrenciesForCheckout(
      response.acceptedCurrencies,
      response.defaultCurrency,
    ),
    defaultCurrency: response.defaultCurrency,
    commerceConfigJson: response.commerceConfigJson ?? null,
    promotionsProgramEnabled: resolvePromotionsProgramEnabled({
      commerceCapabilitiesPromotionsProgramEnabled:
        response.commerceCapabilities?.promotionsProgramEnabled,
      commerceConfigJson: response.commerceConfigJson ?? null,
    }),
    enabledMobileFirstCheckoutSubModes:
      response.enabledMobileFirstCheckoutSubModes ?? [
        ...DEFAULT_ENABLED_MOBILE_FIRST_CHECKOUT_SUB_MODES,
      ],
    paymentRailsMobile: {
      bankTransfer: response.paymentRailsMobile?.bankTransfer ?? false,
      gateway: response.paymentRailsMobile?.gateway ?? false,
    },
    payableVerifiedMethodCount: response.payableVerifiedMethodCount,
    paymentSurfaceReadiness: response.paymentSurfaceReadiness,
    payOnSpotProximityNoticeMinutes: response.payOnSpotProximityNoticeMinutes ?? 85,
    allowedPickupHandoffModes: response.allowedPickupHandoffModes ?? [
      'AUTO_ON_PAYMENT',
      'STAFF_SCAN',
    ],
    defaultPickupHandoffMode: response.defaultPickupHandoffMode ?? 'STAFF_SCAN',
    isActive: response.isActive,
    redirectTo: response.redirectTo,
  };
}

export interface ShopKioskBySlugResponse {
  kioskId: number;
  code: string | null;
  name: string;
  nameLocales?: LocalizedNameMap | null;
  location: string | null;
  /** A20 / A13b — Shipping-only i18n when ONLINE_ONLY (location always null). */
  customerPresence: SalesPointCustomerPresence;
  kioskOperationalMode: 'PRODUCTS' | 'DONATION';
  salesPointInteractionMode?: 'CUSTOMER_FACING' | 'STAFF_OPERATED';
  currencyAcceptanceMode?: 'SINGLE' | 'MULTI';
  acceptedCurrencies?: readonly string[];
  defaultCurrency?: string;
  commerceConfigJson?: Record<string, unknown> | null;
  /** SP green-light for typed promo apply (from commerceCapabilities or legacy config). */
  promotionsProgramEnabled: boolean;
  enabledMobileFirstCheckoutSubModes?: Array<CheckoutSubMode>;
  paymentRailsMobile?: {
    bankTransfer: boolean;
    gateway: boolean;
  };
  payableVerifiedMethodCount?: number;
  paymentSurfaceReadiness?: PaymentSurfaceReadiness;
  payOnSpotProximityNoticeMinutes?: number;
  allowedPickupHandoffModes?: Array<
    'AUTO_ON_PAYMENT' | 'CUSTOMER_TAP' | 'SCAN_AT_STAND' | 'STAFF_SCAN'
  >;
  defaultPickupHandoffMode?: 'AUTO_ON_PAYMENT' | 'CUSTOMER_TAP' | 'SCAN_AT_STAND' | 'STAFF_SCAN';
  isActive: boolean;
  redirectTo?: string;
}

export type ShopKioskByIdResponse = ShopKioskBySlugResponse;

export function useShopConfig(
  kioskId: number | null
): UseQueryResult<KioskPublicCatalogMedia, Error> {
  const api = useApiClient();
  const tenantCode = useTenantCode();

  return useQuery({
    queryKey: customerQueryKeys.shopConfig(tenantCode, kioskId ?? 0),
    queryFn: async () => {
      const data = await api.request<CustomerShopConfigResponse>(
        '/customer/shop-config',
        {
          method: 'GET',
          query: { salesPointId: kioskId ?? undefined },
        }
      );
      return data.catalogMedia;
    },
    enabled: kioskId !== null && kioskId > 0,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: shouldRefetchCustomerOnReconnect,
  });
}

export function useShopProducts(
  kioskId: number | null
): UseQueryResult<CatalogProduct[], Error> {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const tenantCode = useTenantCode();
  const catalogEtagRef = useRef<string | null>(null);
  const cacheKey = useMemo(
    () => customerQueryKeys.shopProducts(tenantCode, kioskId ?? 0),
    [tenantCode, kioskId]
  );

  const clearCatalogEtag = useCallback((): void => {
    catalogEtagRef.current = null;
  }, []);

  const sseConnected = useShopCatalogRealtimeSync(kioskId, cacheKey, clearCatalogEtag);

  return useQuery({
    ...buildShopProductsQueryOptions(kioskId, cacheKey, sseConnected),
    queryFn: async ({ queryKey }) => {
      // G9 — when SSE is denied/disconnected, never stick on If-None-Match.
      // Poll path must full-GET so catalog recovers even if a mutation failed to
      // bump the server ETag (backend G1–G7 should bump; this is defense-in-depth).
      // When SSE is connected, ETag is cleared on connect-refetch / event paths via
      // onBeforeCatalogRefetch (clearCatalogEtag).
      if (!sseConnected) {
        catalogEtagRef.current = null;
      }
      const result = await api.getConditional<ProductsApiResponse | CatalogProduct[]>(
        '/products',
        {
          query: { salesPointId: kioskId ?? undefined },
          ifNoneMatch: sseConnected
            ? (catalogEtagRef.current ?? undefined)
            : undefined,
        },
      );
      if (result.status === 304) {
        const cached = queryClient.getQueryData<CatalogProduct[]>(queryKey);
        if (cached !== undefined) {
          return cached;
        }
        // 304 without a populated cache is not a catalog failure — never throw
        // MISSING_DATA here (image onError invalidation + 304 used to flip the
        // shop into a retry/error storm).
        return [];
      }
      if (result.etag) {
        catalogEtagRef.current = result.etag;
      }
      const data = result.data;
      if (data === undefined) {
        throw new ApiError(
          resolvePublicApiFailureMessage({
            status: result.status || 500,
            code: 'MISSING_DATA',
          }),
          result.status || 500,
          'MISSING_DATA',
        );
      }
      if (Array.isArray(data)) {
        return data;
      }
      return data.products;
    },
  });
}

export function useShopKioskBySlug(
  slug: string | null
): UseQueryResult<ShopKioskBySlugResponse, Error> {
  const api = useApiClient();
  const tenantCode = useTenantCode();
  return useQuery({
    queryKey:
      slug === null
        ? customerQueryKeys.shopKioskBySlug(tenantCode, '__none__')
        : customerQueryKeys.shopKioskBySlug(tenantCode, slug),
    queryFn: async () => {
      if (slug === null) {
        throw new ApiError(
          resolvePublicApiFailureMessage({ status: 400, code: 'MISSING_DATA' }),
          400,
          'MISSING_DATA',
        );
      }
      const response = await api.request<CustomerSalesPointApiResponse>(
        `/customer/sales-points/by-slug/${encodeURIComponent(slug)}`,
        {
        method: 'GET',
      });
      return mapSalesPointToShopKiosk(response);
    },
    enabled: slug !== null && slug.length > 0,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: shouldRefetchCustomerOnReconnect,
  });
}

export function useShopKioskById(
  kioskId: number | null
): UseQueryResult<ShopKioskByIdResponse, Error> {
  const api = useApiClient();
  const tenantCode = useTenantCode();
  return useQuery({
    queryKey: customerQueryKeys.shopKioskById(tenantCode, kioskId ?? -1),
    queryFn: async () => {
      if (kioskId === null || kioskId <= 0) {
        throw new ApiError(
          resolvePublicApiFailureMessage({ status: 400, code: 'MISSING_DATA' }),
          400,
          'MISSING_DATA',
        );
      }
      const response = await api.request<CustomerSalesPointApiResponse>(
        `/customer/sales-points/by-id/${encodeURIComponent(String(kioskId))}`,
        {
          method: 'GET',
        }
      );
      return mapSalesPointToShopKiosk(response);
    },
    enabled: kioskId !== null && kioskId > 0,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: shouldRefetchCustomerOnReconnect,
  });
}

interface BarcodeLookupVars {
  barcode: string;
  kioskId: number;
  /** Session/display currency — BE resolves ProductPrice for this code. */
  currency?: string;
  idempotencyKey?: string;
}

export function useLookupBarcode(): UseMutationResult<
  BarcodeLookupResult,
  Error,
  BarcodeLookupVars
> {
  const api = useApiClient();
  return useMutation({
    mutationFn: ({ barcode, kioskId, currency, idempotencyKey }) =>
      api.request<BarcodeLookupResult>('/products/lookup-barcode', {
        method: 'POST',
        // Backend schema requires `kioskId` as a string of digits, not a
        // number — see file header.
        body: {
          barcode,
          kioskId: String(kioskId),
          ...(currency !== undefined && currency.trim().length > 0
            ? { currency: currency.trim().toUpperCase() }
            : {}),
        },
        idempotencyKey,
      }),
  });
}
